import { useFormErrorSelector, useFormFullSelector } from 'mrf/redux/ReduxHelper';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { generateHtmlId } from '../utils/StringGenerators';
import capitalize from '../utils/capitalize';
import { FormContext } from './FormCRUD';
import FormFieldWrapper from './FormFieldWrapper';

FormFieldNonMemoed.propTypes = {
    context: PropTypes.object,

    label: PropTypes.string,
    field: PropTypes.string.isRequired, // Field name.
    ownedBy: PropTypes.string, // For owned entities, like Address
    component: PropTypes.any.isRequired,
    eventMapper: PropTypes.func.isRequired, // Map an event to a value.
    validateOperation: PropTypes.func, // Callback to validate the new value
    tooltip: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    htmlId: PropTypes.string,
    wrapperClassNames: PropTypes.string, // Optional property for wrapper div to fix some layouts
    required: PropTypes.bool, // Whether this field is required or not
    alwaysUndefinedValue: PropTypes.bool, // If this is enabled, the Component's value will always be ''
    disableTranslation: PropTypes.bool, // Determines if the label will be translated or not, defaults to False
    debugConfigurableRequired: PropTypes.bool, // Shows debug information for Configurable Required
    useFieldNameOnlyForConfigurableRequired: PropTypes.bool // Use just the field name on Configurable Required checks
}

function FormFieldNonMemoed({
    context,
    label,
    field,
    ownedBy,
    component: Component,
    eventMapper,
    validateOperation,
    htmlId: htmlIdOverride,
    tooltip = '',
    tooltipPlacement,
    required,
    alwaysUndefinedValue,
    actions,
    disabled,
    span,
    disableTranslation,
    debugConfigurableRequired = false,
    useFieldNameOnlyForConfigurableRequired = false,
    ...props
}) {
    const formContext = useContext(FormContext);
    if (!context) {
        context = formContext;
    }

    const prefix = useMemo(
        () => context.resource ? [context.resource, ...context.keys].join('_') : 'FormField',
        [context.resource, context.keys]
    );

    const htmlId = useMemo(
        () => htmlIdOverride ?? generateHtmlId(prefix, field),
        [htmlIdOverride, prefix, field]
    );

    let { data, set } = useFormFullSelector(context, field);
    if (alwaysUndefinedValue) {
        data = undefined;
    }

    const fieldData = data?.[field];

    const readOnly = context?.readOnly;

    const errors = useFormErrorSelector(context, context.exactErrorKeys ? false : capitalize(field), fieldData !== undefined);

    const configurableAttributes = useSelector(state => state?.resources?.configurableAttributes)

    const configurableRequired = useMemo(
        () => {
            if (!configurableAttributes) {
                return required;
            }

            const pluralizeName = fieldName => {
                fieldName = fieldName.toLowerCase()
                const parts = fieldName.split(".")
                if (parts.length < 2) {
                    return fieldName
                }
                const actualFieldName = parts[parts.length - 1]
                const modelName = parts[parts.length - 2]
                const stringBuilder = parts.slice(1, parts.length - 2)
                stringBuilder.push(pluralize(modelName))
                stringBuilder.push(actualFieldName)
                
                if (debugConfigurableRequired){
                    console.log(`attribute fieldName: ${fieldName}`)
                    console.log(`actualFieldName: ${actualFieldName}`)
                    console.log(`modelName: ${modelName}`)
                    console.log(`stringBuilder: ${stringBuilder.join('.')}`)
                }
                
                if (useFieldNameOnlyForConfigurableRequired){
                    return actualFieldName
                }
                
                return stringBuilder.join(".").toString()
            }

            const fieldName = useFieldNameOnlyForConfigurableRequired ? field : `${context?.resource}.${field}`;

            const configurableAttributesList = configurableAttributes.list?.map(id => configurableAttributes.byId[id])
            const configurableAttribute = configurableAttributesList?.find(attr => pluralizeName(attr.fieldName) === fieldName.toLowerCase())
            
            if(debugConfigurableRequired){
                console.log(`front fieldName: ${fieldName}`);
            }

            if (!configurableAttribute) {
                return required;
            }

            return configurableAttribute.isRequired;
        },
        [required, context?.resource, field, configurableAttributes, debugConfigurableRequired, useFieldNameOnlyForConfigurableRequired]
    )

    // Default function to handle field changes.
    // useCallback ensures that only if the inputs change, the function object will be recreated
    const inputOnChange = useCallback(
        async e => {
            // Map the new value using a mapper provider by client
            const newValue = eventMapper(e);
            // Validate the field using a validator provided by client
            if (validateOperation?.(newValue) === false) {
                return;
            }
            // Write the new value and mark the model object as dirty
            set(
                { [field]: newValue?.then ? await newValue : newValue },
                { dirty: true },
            );
        },
        [eventMapper, field, set, validateOperation] // Inputs to evaluate if a new function must be created.
    );

    return (
        <FormFieldWrapper
            context={context}
            label={label}
            field={field}
            ownedBy={ownedBy}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            required={configurableRequired}
            alwaysUndefinedValue={alwaysUndefinedValue}
            actions={actions}
            disabled={disabled}
            span={span}
            disableTranslation={disableTranslation}
            {...props}
        >
            <Component
                id={htmlId}
                htmlId={htmlId}
                value={data ?? ''}
                onChange={inputOnChange}
                error={errors}
                field={field}
                readOnly={readOnly}
                disabled={disabled}
                required={undefined}
                {...props}
            />
        </FormFieldWrapper>
    );
}

const FormField = React.memo(FormFieldNonMemoed);
export default FormField;
